import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, TrendingUp, TrendingDown, Wallet, Clock } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#0a0a0a", "#E0B94B", "#6b7280", "#b45309", "#1d4ed8", "#059669", "#9333ea"];
const fmt = (n) => `€${(n ?? 0).toFixed(2)}`;
const monthLabel = (k) => new Date(k + "-01").toLocaleDateString("it-IT", { month: "short" });

function Stat({ label, value, icon: Icon, tone = "", testid }) {
  return (
    <div className={`rounded-2xl p-4 ${tone || "bg-neutral-50"}`}>
      <div className="flex items-center gap-1.5 text-xs opacity-70 mb-1"><Icon size={14} />{label}</div>
      <p data-testid={testid} className="font-display text-2xl font-extrabold">{value}</p>
    </div>
  );
}

function Donut({ data, testid }) {
  if (!data.length) return <p className="text-xs text-neutral-400 py-6 text-center">Nessun movimento.</p>;
  return (
    <div data-testid={testid} className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => fmt(v)} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CashflowPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/me/cashflow").then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  let running = 0;
  const trend = (data?.months || []).map((m) => {
    const volume = m.in + m.out;
    running += volume;
    return { label: monthLabel(m.month), volume: +volume.toFixed(2), cumulative: +running.toFixed(2), average: +(volume / 2).toFixed(2) };
  });

  return (
    <div data-testid="cashflow-page" className="p-4 md:p-0 space-y-5">
      <div className="flex items-center gap-2">
        <button data-testid="cashflow-back" onClick={() => navigate("/profilo")} className="p-2 -ml-2 rounded-full hover:bg-neutral-100"><ArrowLeft size={20} /></button>
        <div>
          <h1 className="font-display text-xl font-extrabold">Flusso di denaro e statistiche</h1>
          <p className="text-xs text-neutral-500">{isAdmin ? "Commissioni ENBISSY incassate, rilasci a venditori e promotori" : "Le tue entrate (vendite, provvigioni) e uscite (acquisti, abbonamenti)"}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stat label="Entrate totali" value={fmt(data?.total_in)} icon={TrendingUp} tone="bg-black text-white" testid="cf-total-in" />
        <Stat label="Uscite totali" value={fmt(data?.total_out)} icon={TrendingDown} testid="cf-total-out" />
        <Stat label="Saldo netto" value={fmt(data?.net)} icon={Wallet} tone={data && data.net < 0 ? "bg-red-50 text-red-700" : "bg-[#E0B94B]/15"} testid="cf-net" />
        {isAdmin
          ? <Stat label="Volume transato" value={fmt(data?.volume)} icon={Wallet} testid="cf-volume" />
          : <Stat label="In attesa di rilascio" value={fmt(data?.payout_pending)} icon={Clock} testid="cf-payout-pending" />}
      </div>

      {data && data.declared_in > 0 && (
        <p data-testid="cf-declared" className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3">
          Importi dichiarati fuori Stripe (vendite esterne, non incassati): €{data.declared_in.toFixed(2)} — esclusi dai totali qui sopra.
        </p>
      )}

      <div className="border border-neutral-200 rounded-2xl p-4">
        <h2 className="font-display font-bold mb-3">Ultimi 6 mesi</h2>
        <div data-testid="cf-monthly-chart" className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={(data?.months || []).map((m) => ({ ...m, label: monthLabel(m.month) }))} barGap={2}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} width={44} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="in" name="Entrate" fill="#0a0a0a" radius={[6, 6, 0, 0]} />
              <Bar dataKey="out" name="Uscite" fill="#E0B94B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-neutral-200 rounded-2xl p-4">
        <h2 className="font-display font-bold mb-1">Andamento del volume nel tempo</h2>
        <p className="text-xs text-neutral-500 mb-3">Somma di entrate e uscite (volume mensile e cumulato) e media tra entrate e uscite.</p>
        <div data-testid="cf-trend-chart" className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} width={44} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="cumulative" name="Volume cumulato (somma)" stroke="#0a0a0a" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="volume" name="Volume mensile (entrate + uscite)" stroke="#E0B94B" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="average" name="Media (entrate + uscite) / 2" stroke="#6b7280" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-neutral-200 rounded-2xl p-4">
          <h2 className="font-display font-bold mb-1">Entrate per categoria</h2>
          <Donut data={data?.by_category_in || []} testid="cf-in-donut" />
        </div>
        <div className="border border-neutral-200 rounded-2xl p-4">
          <h2 className="font-display font-bold mb-1">Uscite per categoria</h2>
          <Donut data={data?.by_category_out || []} testid="cf-out-donut" />
        </div>
      </div>

      <div className="border border-neutral-200 rounded-2xl p-4">
        <h2 className="font-display font-bold mb-2">Movimenti <span className="text-neutral-400 font-normal text-sm">({data?.movements_count ?? 0})</span></h2>
        <div className="divide-y divide-neutral-100">
          {(data?.movements || []).map((m, i) => (
            <div key={i} data-testid={`cf-movement-${i}`} className="flex items-center justify-between py-2 text-sm">
              <div className="min-w-0"><p className="font-medium truncate">{m.title}</p><p className="text-xs text-neutral-400">{m.category} · {new Date(m.created_at).toLocaleDateString("it-IT")}</p></div>
              <p className={`font-display font-bold ${m.kind === "in" ? "text-green-700" : "text-neutral-800"}`}>{m.kind === "in" ? "+" : "−"}{fmt(m.amount)}</p>
            </div>
          ))}
          {data && data.movements.length === 0 && <p data-testid="cf-empty" className="text-xs text-neutral-400 py-4">Nessun movimento ancora. Comparirà qui il primo pagamento.</p>}
        </div>
      </div>
    </div>
  );
}
